import React, { useEffect, useState, useMemo } from "react";
import "./SubjectWisePage.scss";
import images from "../../../../utilities/images/images";
import { useDispatch, useSelector } from "react-redux";
import Performance from "../OverallPage/Performance/Performance";
import Subjectchart from "../../components/chart/subjectchart";
import { fetchSubjectWiseAnalysisRequest } from "../../../../redux/exam_analytics/analyticsSlice";
import { ClipLoader } from "react-spinners";

const SubjectWisePage = () => {
  const dispatch = useDispatch();

  // ⭐ KEEP existing fields + add paperId (NO removal)
  const { testDetails, testType, paperId } = useSelector(
    (state) => state.examSlice
  );

  const analyticsState = useSelector((state) => state.analyticsSlice || {});
  const loading = analyticsState?.loading?.subject || false;
  const subjectWiseAnalysis = analyticsState?.subjectWiseAnalysis || null;

  const subjectsData = useMemo(() => {
    if (!subjectWiseAnalysis) return [];
    if (Array.isArray(subjectWiseAnalysis)) return subjectWiseAnalysis;
    if (Array.isArray(subjectWiseAnalysis?.subjects))
      return subjectWiseAnalysis.subjects;
    if (Array.isArray(subjectWiseAnalysis?.data?.subjects))
      return subjectWiseAnalysis.data.subjects;
    return [];
  }, [subjectWiseAnalysis]);

  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");

  useEffect(() => {
    // ✅ SAFE FIX: stable paperId + backward compatibility
    const finalPaperId = paperId || testDetails?.paper_id;
    const finalSetId = testDetails?.setId;
    const paperType = testType?.id;

    // if (finalPaperId && paperType) {
    if ((finalPaperId || finalSetId) && paperType) {
      dispatch(
        fetchSubjectWiseAnalysisRequest({
          paperId: finalPaperId,
          setId: finalSetId,
          paperType,
        })
      );
    }
  }, [dispatch, paperId, testDetails?.paper_id, testType?.id]);

  const subjects = useMemo(() => {
    return subjectsData?.map((s) => s?.subject)?.filter(Boolean) || [];
  }, [subjectsData]);

  useEffect(() => {
    if (subjects.length && !selectedSubject) {
      const defaultSub =
        subjects.find((s) => s !== "All Subjects") || subjects[0];
      setSelectedSubject(defaultSub);
    }
  }, [subjects, selectedSubject]);

  const levels = useMemo(() => {
    const data = subjectsData?.find((s) => s?.subject === selectedSubject);
    return data?.levels?.map((l) => l.level) || [];
  }, [selectedSubject, subjectsData]);

  useEffect(() => {
    if (levels?.length && !selectedLevel) {
      setSelectedLevel(levels[0]);
    }
  }, [levels, selectedLevel]);

  const filteredLevelData = useMemo(() => {
    if (!selectedSubject || !selectedLevel) return null;
    const subjectBlock = subjectsData?.find(
      (s) => s.subject === selectedSubject
    );
    return subjectBlock?.levels?.find((l) => l.level === selectedLevel) || null;
  }, [selectedSubject, selectedLevel, subjectsData]);

  const parseScore = (val) => {
    if (typeof val === "number") return val;
    if (!val || typeof val !== "string") return 0;
    const match = val.match(/-?\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : 0;
  };

  const formattedTopicData = useMemo(() => {
    if (!filteredLevelData) return null;

    return {
      score_out_of: filteredLevelData?.your_score ?? 0,
      percentile: filteredLevelData?.percentile ?? 0,
      accuracy: filteredLevelData?.accuracy ?? 0,
      duration: filteredLevelData?.duration ?? 0,
      avg_time_per_qn: filteredLevelData?.tpq_avg ?? 0,
    };
  }, [filteredLevelData]);

  const formattedPerformance = useMemo(() => {
    if (!filteredLevelData?.view_performance_by) return [];

    return filteredLevelData.view_performance_by.map((item) => ({
      label: item?.label || "",
      average: parseScore(item?.average_students),
      your: parseScore(item?.your_performance),
      topper: parseScore(item?.topper_students),
      totalPossible:
        item?.label === "Score"
          ? parseInt(item?.average_students?.split("/")[1]) || null
          : null,
    }));
  }, [filteredLevelData]);

  if (loading) {
    return (
      <div className="subjectwise-header subjectwise-loading">
        <ClipLoader color="#C61C26" size={50} />
      </div>
    );
  }

  if (!subjectsData?.length) {
    return (
      <div className="subjectwise-header">
        <p>No Data Found</p>
      </div>
    );
  }

  return (
    <div className="subjectwise-header">
      <div className="subjectwise-difficulty-header">
        <h3 className="subjectwise-difficulty-heading">
          Difficulty Analysis
        </h3>
        <img
          src={images.new_report}
          alt="info"
          className="subjectwise-difficulty-info-icon"
        />
      </div>

      <div className="subjectwise-difficulty-filters">
        <div className="subjectwise-difficulty-filter-item">
          <label>Subject:</label>
          <select
            value={selectedSubject}
            onChange={(e) => {
              setSelectedSubject(e.target.value);
              setSelectedLevel("");
            }}
          >
            {subjects?.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="subjectwise-difficulty-filter-item">
          <label>Difficulty Level:</label>
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
          >
            {levels?.map((lvl) => (
              <option key={lvl} value={lvl}>
                {lvl}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="subjectwise-body-card">
        {filteredLevelData ? (
          <>
            <div className="subjectwise-body-card-chart">
              <Subjectchart topic={formattedTopicData} />
            </div>

            <div className="subjectwise-performance-card">
              <Performance
                performanceData={formattedPerformance}
                totalPossible={formattedPerformance?.[0]?.totalPossible}
              />
            </div>
          </>
        ) : (
          <p className="no-data-text">
            Select a subject and difficulty level
          </p>
        )}
      </div>
    </div>
  );
};

export default SubjectWisePage;
